import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, IconButton, LinearProgress } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useAuth0 } from '@auth0/auth0-react';

import { FDropzone, FHInputField, FHTextarea, FHTooltip } from '../../../../../../../../../base';
import { useTreatmentPlanEditable } from '../../../../../../../hooks/useTreatmentPlanEditable';
import {
  validateSync,
  getAllInvalidFieldNames,
} from '../../../../../../../../../../helpers/validation-helpers';
import { debounce } from '../../../../../../../../../../helpers';
import { visualSchema } from '../../../../../../utils/validationSchema';
import { MINIMUM_REQUIRED_SENTENCE_LENGTH, Themes } from '../../../../../../../../../../constants';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../../../../../../../utils/colors';
import { assessmentActions } from '../../../../../../../../../../store';
import { ERROR_STATES, PERSIST_DATA_STATES, STATE_IDS } from '../../../../../../constants/states';
import TrashIcon from '../../../../../../../../../svgs/TrashIcon3';

import type { ChangeEvent } from 'react';
import type { IAssessmentVisual } from '../../../../../../../../../../utils/types';

interface VisualProps {
  visual: IAssessmentVisual;
  toolId?: number;
  onRemove?: () => void;
}

function Visual({ visual, toolId, onRemove }: VisualProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { document_url } = visual;

  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { assessment, persistDataState } = useSelector((state: any) => state.assessments);

  const [visualInfo, setVisualInfo] = useState<IAssessmentVisual>({
    label: '',
    description: '',
    document_url: document_url,
  });
  const [errors, setErrors] = useState<any>({});
  const [initialDocumentUrl, setIntialDocumentUrl] = useState(() => document_url);
  const [imageChangeInProgress, setImageLoadInProgress] = useState(false);
  const [showEditVisual, setShowEditVisual] = useState<boolean>(false);

  const editable = useTreatmentPlanEditable();

  useEffect(() => {
    if (!visual || !treatmentPlan?.id) return;

    const persistedData =
      persistDataState[
        `${PERSIST_DATA_STATES[STATE_IDS.VISUAL]}-${toolId}-${visual.id ? visual.id : 'new'}`
      ];

    if (persistedData) {
      setVisualInfo(persistedData);
      const data = { label: persistedData.label, description: persistedData.description };
      const errorsData = getAllInvalidFieldNames(visualSchema, data);
      setErrors(errorsData);
    } else {
      if (visual?.document_url !== initialDocumentUrl) {
        setImageLoadInProgress(true);
      }
      setVisualInfo(visual);
      setIntialDocumentUrl(visual?.document_url);
      handleUpdateField('document_url', visual.document_url, visual);
    }
    // eslint-disable
  }, [visual?.document_url]);

  useEffect(() => {
    if (!treatmentPlan?.id) return;
    debouncedErrorUpdater(errors);
    // eslint-disable
  }, [errors]);

  const handleDeleteVisual = () => {
    if (!visual?.id || !visual?.tool_id || !treatmentPlan?.id || !assessment?.id) {
      setErrors({});
      if (onRemove) {
        onRemove();
      }
    } else {
      dispatch(
        assessmentActions.deleteToolDocument({
          getAccessTokenSilently,
          planId: treatmentPlan.id,
          assessmentId: assessment.id,
          toolId: visual.tool_id,
          documentId: visual.id,
        }),
      );
    }
  };

  const updateErrorCount = (errors: any) => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [`${ERROR_STATES[STATE_IDS.VISUAL]}-${toolId}-${visual.id ? visual.id : 'new'}`]:
            errorCount,
        },
      }),
    );
  };
  const updatePersistedData = (visualData: IAssessmentVisual, toolId: number) => {
    const isValid = validateSync(visualSchema, visualData);
    const data = isValid ? null : visualData;
    dispatch(
      assessmentActions.updatePersistDataState({
        data: {
          [`${PERSIST_DATA_STATES[STATE_IDS.VISUAL]}-${toolId}-${visual.id ? visual.id : 'new'}`]:
            data,
        },
      }),
    );
  };

  const debouncedErrorUpdater = useCallback(debounce(updateErrorCount, 500), []);
  const debouncedDataPersister = useCallback(debounce(updatePersistedData, 500), []);

  const handleValidate = (data: any = visualInfo) => {
    if (!data) return;

    const invalidFields = getAllInvalidFieldNames(visualSchema, {
      label: data.label,
      description: data.description,
    });
    setErrors(invalidFields);
  };

  const handleUpdateField = (key: string, value: any, data = visualInfo) => {
    const updatedData = { ...data, [key]: value };
    setVisualInfo(updatedData);
    debouncedDataPersister(updatedData, toolId);
    handleValidate(updatedData);
  };

  const handleSelectFiles = (files: any) => {
    const file = files[0];
    if (!file) {
      setShowEditVisual(false);
      return;
    }
    if (!treatmentPlan?.id || !assessment?.id || !toolId) return;

    const data = new FormData();

    data.append('file', file);
    dispatch(
      assessmentActions.updateToolDocumentImage({
        data,
        getAccessTokenSilently,
        planId: treatmentPlan.id,
        assessmentId: assessment.id,
        toolId,
        documentId: visualInfo.id,
      }),
    );
  };

  const handleCreateOrUpdateVisual = () => {
    const isValid = validateSync(visualSchema, visualInfo);
    if (!isValid || !treatmentPlan?.id || !assessment?.id || !toolId) return;

    if (!visualInfo?.id) {
      const data = new FormData();

      data.append('file', visualInfo.document_contents);
      data.append('label', visualInfo.label);
      data.append('description', visualInfo.description);
      dispatch(
        assessmentActions.createToolDocument({
          data,
          getAccessTokenSilently,
          planId: treatmentPlan.id,
          assessmentId: assessment.id,
          toolId,
        }),
      );
    } else {
      const data = new URLSearchParams({
        label: visualInfo.label,
        description: visualInfo.description,
      });
      dispatch(
        assessmentActions.updateToolDocument({
          data,
          getAccessTokenSilently,
          planId: treatmentPlan.id,
          assessmentId: assessment.id,
          toolId,
          documentId: visualInfo.id,
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: FORTA_WHITE,
        borderRadius: '10px',
        p: 3,
        mt: 3,
      }}
    >
      {imageChangeInProgress && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <LinearProgress />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {editable && (
          <Box sx={{ border: `1px solid ${FORTA_BLUE.primary}`, borderRadius: '8px', px: 1 }}>
            <FHTooltip
              arrow
              title={'Delete Visual'}
              placement="top"
              sx={{
                borderRadius: '10px',
              }}
              tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
            >
              <IconButton disabled={imageChangeInProgress} onClick={handleDeleteVisual}>
                <TrashIcon data-testid="deleteVisual" width={18} height={18} />
              </IconButton>
            </FHTooltip>
            {visual?.id && (
              <FHTooltip
                arrow
                title={'Update Image'}
                placement="top"
                sx={{
                  borderRadius: '10px',
                }}
                tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
              >
                <IconButton
                  disabled={imageChangeInProgress}
                  onClick={() => {
                    setShowEditVisual(true);
                  }}
                >
                  <EditOutlinedIcon sx={{ width: 21, height: 21 }} data-testid="editVisual" />
                </IconButton>
              </FHTooltip>
            )}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          mt: 3,
        }}
      >
        <Box
          data-testid="visualPreview"
          sx={{
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column',
            width: '100%',
            height: 500,
            marginBottom: '1em',
          }}
        >
          {visualInfo.file_type === 'pdf' ||
          visualInfo.noPreviewFile ||
          visual.file_type === 'pdf' ||
          visual.noPreviewFile ? (
            <Typography fontWeight={'bold'}>Preview not available</Typography>
          ) : (
            <img
              src={visualInfo.document_url ?? visual.document_url}
              alt={'visual'}
              onLoad={() => {
                if (imageChangeInProgress) {
                  setImageLoadInProgress(false);
                }
              }}
              height={'100%'}
              style={{ objectFit: 'contain' }}
            />
          )}
        </Box>
        <Box>
          <Typography textAlign="left" fontWeight="bold" mb={0.5}>
            Visual title:*
          </Typography>
          <FHInputField
            data-testid="visualTitleTextField"
            value={visualInfo.label}
            sx={{ marginBottom: '1em' }}
            fullWidth
            disabled={!editable || imageChangeInProgress}
            inputHeight={40}
            colorTheme={Themes.Primary}
            onChange={(e) => handleUpdateField('label', e.target.value)}
            onBlur={handleCreateOrUpdateVisual}
            error={errors['label']}
            coloredBackground
          />
        </Box>
        <Box>
          <Typography textAlign="left" fontWeight="bold" mb={0.5}>
            Visual analysis:*
          </Typography>
          <FHTextarea
            data-testid="visualAnalysisTextField"
            fullWidth
            minRows={3}
            maxRows={6}
            colorTheme={Themes.Primary}
            disabled={!editable || imageChangeInProgress}
            expandable={editable}
            coloredBackground
            minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
            value={visualInfo.description}
            onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
              handleUpdateField('description', e.target.value)
            }
            onBlur={handleCreateOrUpdateVisual}
            onCloseExpandDialog={handleCreateOrUpdateVisual}
            error={errors['description']}
            helperText={
              errors['description'] ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters` : ''
            }
          />
        </Box>
      </Box>
      <FDropzone
        colorTheme={Themes.Primary}
        show={showEditVisual}
        title="Update Image"
        subTitle=" "
        submitText="Save image"
        fileNameRegEx={'^[a-zA-Z0-9_., -]*$'}
        maxSize={10}
        accept={{
          'image/png': ['.png'],
          'image/jpeg': ['.jpeg'],
          'image/jpg': ['.jpg'],
        }}
        noPreviewFiles={['.pdf']}
        onSelect={handleSelectFiles}
        onClose={() => setShowEditVisual(false)}
      />
    </Box>
  );
}

export default Visual;
